import { Input, Typography, UtilityButton } from '@quickbit/qb-design-system';
import { QRCodeCanvas } from 'qrcode.react';
import { useState } from 'react';
import { useAsync, useAsyncFn } from 'react-use';
import { fetchAPI } from 'api';
import { ErrorComponent, Layout } from 'components';

export const Authenticator = () => {
  const [code, setCode] = useState('');
  const [setupUrl, setSetupUrl] = useState('');
  const [activated, setActivated] = useState(false);
  const {
    value: status,
    loading,
    error,
  } = useAsync(
    () => fetchAPI<{ enabled: boolean }>('authenticator', { method: 'GET' }),
    []
  );
  const [{ loading: starting, error: setupError }, start] =
    useAsyncFn(async () => {
      const result = await fetchAPI<{ url: string }>('authenticator', {
        method: 'POST',
        body: {},
      });
      setSetupUrl(result.url);
    }, []);
  const [{ loading: saving, error: activationError, value: result }, activate] =
    useAsyncFn(async () => {
      const response = await fetchAPI<{ success: boolean }>('authenticator', {
        method: 'POST',
        body: { code },
      });
      if (response.success) {
        setActivated(true);
        setSetupUrl('');
        setCode('');
      }
      return response;
    }, [code]);

  return (
    <Layout title="Authenticator" backNavigationPath={-1}>
      {(error || setupError || activationError) && (
        <ErrorComponent error={error || setupError || activationError} />
      )}
      {status?.enabled || activated ? (
        <Typography variant="body">
          Your authenticator is enabled. Use its code when signing in with your
          password.
        </Typography>
      ) : (
        <>
          <Typography variant="body">
            Set up an authenticator app before SMS login is retired. Keep access
            to your existing eID method. If you lose your authenticator, contact
            support for identity verification and recovery.
          </Typography>
          {!setupUrl && (
            <UtilityButton
              text="Set up authenticator"
              onClick={start}
              disabled={loading || starting || !status}
            />
          )}
          {setupUrl && (
            <>
              <Typography variant="body">
                Scan this QR code or open the link on a device with your
                authenticator app. This setup link is private; do not share it.
              </Typography>
              <QRCodeCanvas value={setupUrl} size={200} />
              <a href={setupUrl}>Add Quickbit to your authenticator</a>
              <Input
                label="Six-digit authenticator code"
                value={code}
                onChange={(event) =>
                  setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                }
              />
              <UtilityButton
                text="Confirm setup"
                onClick={activate}
                disabled={saving || code.length !== 6}
              />
              {result && !result.success && (
                <Typography variant="body">
                  The code was incorrect. Try the current code from your
                  authenticator.
                </Typography>
              )}
            </>
          )}
        </>
      )}
    </Layout>
  );
};
